import Emitter from "@homer.core/emitter";
import scheduler from "@homer.core/scheduler";
import taskFetch from "@homer.task/fetch";
import StorageLocal from "@homer.core/storage.local";
import { version as pkgVersion } from "../package.json";

const matchModuleState = /^\[demand:(.+?)\]\[state\]$/;

const filterDependencies = (input, state) => {
    return Object.entries(input)
        .filter(([_, dependency]) => dependency[state])
        .map(([_, dependency]) => dependency.name);
};

const clear = StorageLocal.supported
    ? (version, resolve) => {
          Object.keys(globalThis.localStorage)
              .filter((key) => key.indexOf("[demand:") === 0 && key.endsWith("[state]"))
              .forEach((key) => {
                  scheduler.yield.low().then(
                      resolve
                          ? () => {
                                const name = key.replace(matchModuleState, "$1");
                                const resolved = resolve(name);

                                new StorageLocal(`demand:${resolved?.name || name}`, {
                                    version: resolved?.version || version,
                                    sticky: false,
                                })[resolved ? "get" : "remove"]();
                            }
                          : () => {
                                new StorageLocal(`demand:${key.replace(matchModuleState, "$1")}`, {
                                    version,
                                    sticky: false,
                                }).remove();
                            },
                  );
              });
      }
    : () => {};

const createDeferred = () => {
    const deferred = {};

    deferred.promise = new Promise((resolve, reject) => {
        deferred.resolve = resolve;
        deferred.reject = reject;
    });

    return deferred;
};

class Dependency {
    #private;

    constructor(resolved, options, modules) {
        const { name, version, url, dependencies } = resolved;
        const deferred = createDeferred();

        this.#private = {
            name,
            version,
            url,
            options,
            modules,
            dependencies: Object.freeze(dependencies.map((name) => Dependency.obtain(name, options, modules))),
            deferred,
            promise: deferred.promise,
            storage: new StorageLocal(`demand:${name}`, { version, sticky: false }),
            state: Dependency.isPending,
            type: null,
            source: null,
            required: false,
        };

        options.emit("dependency", this);
    }

    get name() {
        return this.#private.name;
    }

    get version() {
        return this.#private.version;
    }

    get url() {
        return this.#private.url;
    }

    get dependencies() {
        return this.#private.dependencies;
    }

    get type() {
        return this.#private.type;
    }

    get source() {
        return this.#private.source;
    }

    get promise() {
        return this.#private.promise;
    }

    get isLoading() {
        return !!(this.#private.state & Dependency.isLoading);
    }

    get isCacheHit() {
        return !!(this.#private.state & Dependency.isCacheHit);
    }

    get isPending() {
        return !!(this.#private.state & Dependency.isPending);
    }

    get isResolved() {
        return !!(this.#private.state & Dependency.isResolved);
    }

    get isRejected() {
        return !!(this.#private.state & Dependency.isRejected);
    }

    resolve(value) {
        if (this.isPending) {
            const properties = this.#private;
            const { deferred, options } = properties;

            properties.state |= Dependency.isResolved;
            properties.state &= ~Dependency.isPending;
            properties.state &= ~Dependency.isLoading;

            deferred.resolve(value);
            options.emit("resolve", this, value);
        }
    }

    reject(error) {
        if (this.isPending) {
            const properties = this.#private;
            const { deferred, options } = properties;

            properties.state &= ~Dependency.isLoading;

            // Rejections may be undefined or primitive values; keep the original reason intact.
            if (error?.aborted || error instanceof taskFetch.AbortError) {
                options.emit("abort", this);
            }

            properties.state |= Dependency.isRejected;
            properties.state &= ~Dependency.isPending;

            deferred.reject(error);
            options.emit("reject", this, error);
        }
    }

    load(optional = false) {
        const properties = this.#private;

        if (!optional) {
            properties.required = true;
        }

        if (!this.url || this.isLoading || !this.isPending) {
            return properties.promise;
        }

        const { options, storage } = properties;
        let cache;

        properties.state |= Dependency.isLoading;

        if (options.cache && (cache = storage.get())) {
            const index = cache.indexOf(":");
            const type = index !== -1 ? cache.substring(0, index) : null;
            const data = index !== -1 ? cache.substring(index + 1) : null;

            if (type && type !== "null" && type !== "undefined" && data && data !== "null" && data !== "undefined") {
                properties.state |= Dependency.isCacheHit;
                cache = { type, data };
            } else {
                cache = null;
            }
        }

        options.emit(cache ? "hit" : "miss", this);

        const sourceTask = cache || taskFetch.execute(this.url);
        const dependencyTasks = this.dependencies.map((dependency) => dependency.load(optional));
        const remainingTasks = [];

        dependencyTasks.forEach((task) => {
            if (typeof task?.signal === "function") {
                remainingTasks.push(task);
            }
        });

        if (typeof sourceTask?.signal === "function") {
            remainingTasks.push(sourceTask);
        }

        void Promise.all(dependencyTasks).catch((error) => {
            this.reject(error);
        });

        Promise.resolve(sourceTask)
            .then(({ status, type, data }) => {
                if (!this.isPending) {
                    return;
                }

                properties.type = type;
                properties.source = data;

                if ((status && status !== 200) || !data) {
                    throw new Error(
                        `[demand] failed to load package '${this.name}@${this.version}', received ${status} and/or empty response`,
                        { cause: { url: this.url, status, type, data } },
                    );
                }

                return scheduler.yield.medium().then(async () => {
                    const [processed] = await options.process(this);
                    const source = processed == null ? null : String(processed);

                    if (source) {
                        properties.source = source;
                    }

                    options.emit("process", this);
                });
            })
            .then(() => {
                if (!this.isPending) {
                    return;
                }

                if (options.cache && !cache) {
                    scheduler.yield.low().then(() => {
                        storage.set(`${this.type}:${this.source}`);
                        options.emit("cache", this);
                    });
                }
            })
            .catch((error) => {
                this.reject(error);

                if (error instanceof taskFetch.AbortError) {
                    delete properties.modules[properties.name];
                }

                if (properties.required) {
                    // Necessary to pass required dependency failures to the global errorhandler.
                    throw error;
                }
            });

        return Object.defineProperties(properties.promise, {
            abort: {
                value: (reason) => {
                    remainingTasks.forEach((task) => {
                        task.signal(reason);
                    });
                },
                enumerable: true,
            },
        });
    }

    static obtain(name, options, modules) {
        name = name[0] === "?" ? name.substring(1) : name;

        const direct = modules[name];

        if (direct && !direct.isRejected) {
            return direct;
        }

        if (direct) {
            // A new explicit request must be able to retry a previously rejected dependency.
            delete modules[name];
        }

        const resolved = options.resolve(name).shift() || {
            name,
            version: options.version,
            url: null,
            dependencies: [],
        };
        const cached = modules[resolved.name];

        if (cached?.isRejected) {
            delete modules[resolved.name];
        }

        return modules[resolved.name] || (modules[resolved.name] = new Dependency(resolved, options, modules));
    }

    static isLoading = Math.pow(2, 1);
    static isCacheHit = Math.pow(2, 2);
    static isPending = Math.pow(2, 3);
    static isResolved = Math.pow(2, 4);
    static isRejected = Math.pow(2, 5);
}

export default class Demand extends Emitter {
    #private;

    constructor({ resolver, processor, version, cache = true, stub = null }) {
        super();

        Object.defineProperty(this, "clear", {
            value: Object.freeze({
                expired: () => {
                    clear(version, resolver);
                },
                all: () => {
                    clear(version);
                },
            }),
            enumerable: true,
        });

        Object.defineProperty(this.provide, "amd", {
            value: true,
            enumerable: true,
        });

        this.#private = {
            resolver,
            processor,
            modules: {},
            options: {
                version,
                cache,
                emit: this.emit.bind(this),
                resolve: this.resolve.bind(this),
                process: this.process.bind(this),
            },
        };

        if (stub) {
            this.initialize(stub);
        }

        setTimeout(() => {
            scheduler.yield.low().then(() => {
                clear(version, resolver);
            });
        }, 10000);
    }

    initialize({ modules = {}, queue = {} }) {
        Object.entries(modules).forEach(([name, value]) => {
            // Preserve inline stub module values exactly as they are, including bare Promises.
            Dependency.obtain(name, this.#private.options, this.#private.modules).resolve(value);
        });

        Object.entries(queue).forEach(([name, dfd]) => {
            this.request(name)
                .then(([value]) => {
                    dfd.resolve(value);
                })
                .catch(dfd.reject);

            delete queue[name];
        });
    }

    get version() {
        return pkgVersion;
    }

    request(...dependencies) {
        const { options, modules } = this.#private;
        const requests = dependencies.map((dependency) => {
            const optional = dependency[0] === "?";
            const request = Dependency.obtain(dependency, options, modules).load(optional);

            return {
                request,
                promise: optional ? request.catch(() => undefined) : request,
            };
        });

        return Object.defineProperties(Promise.all(requests.map(({ promise }) => promise)), {
            abort: {
                value: (reason) => {
                    requests.forEach(({ request }) => {
                        request.abort(reason);
                    });
                },
                enumerable: true,
            },
        });
    }

    provide(name, ...parameter) {
        const { options, modules } = this.#private;
        const dependency = Dependency.obtain(name, options, modules);
        const factory = parameter.pop();
        const dependencies = parameter.shift() || [];

        // AMD factories must also work without a resolver-preloaded dependency graph.
        return Promise.all(
            dependencies.map((name) => {
                const optional = name[0] === "?";
                // The owning module propagates failures; do not promote children of an optional request to required.
                const task = Dependency.obtain(name, options, modules).load(true);

                return optional ? task.catch(() => undefined) : task;
            }),
        )
            .then((dependencies) => {
                return Promise.resolve(factory(...dependencies));
            })
            .then((module) => {
                dependency.resolve(module);
            })
            .catch((error) => {
                dependency.reject(error);
            });
    }

    resolve(...dependencies) {
        const { resolver } = this.#private;

        return dependencies.map((dependency) => {
            return resolver(dependency) || null;
        });
    }

    process(...dependencies) {
        const { processor } = this.#private;

        return Promise.all(dependencies.map((dependency) => processor(dependency) ?? null));
    }

    get modules() {
        return Object.values(this.#private.modules).map((module) => Object.freeze(module));
    }

    get list() {
        return Object.keys(this.#private.modules);
    }

    get pending() {
        return filterDependencies(this.#private.modules, "isPending");
    }

    get resolved() {
        return filterDependencies(this.#private.modules, "isResolved");
    }

    get rejected() {
        return filterDependencies(this.#private.modules, "isRejected");
    }
}
